import React, { useRef, useEffect, useState } from 'react';
import { MdInsertDriveFile, MdInfoOutline } from 'react-icons/md';

/**
 * MessageDisplay component renders the list of messages in a chat.
 * Uses Inline Styles for styling.
 */

// Helper function to format post content with line breaks
const renderPostContent = (content) => {
    if (typeof content !== 'string') return null;
    const shortContent = content.length > 100 ? content.substring(0, 100) + '...' : content;
    return shortContent.split('\n').map((line, index) => (
        <React.Fragment key={index}>
            {line}
            {index < shortContent.split('\n').length - 1 && <br />}
        </React.Fragment>
    ));
};

const MessageDisplay = ({ messages, user, handleSharedPostClick, isGroup, allUsers, containerHeight }) => {
    const messagesEndRef = useRef(null);

    // Scroll to bottom logic
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Helper to get Sender's Name
    const getSenderName = (senderId) => {
        const sender = allUsers.find(u => u.id === senderId);
        return sender ? (sender.fullName || sender.userName || 'Unknown User') : 'Unknown User';
    };

    // --- Inline Styles ---
    const styles = {
        container: {
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            backgroundColor: '#f3f4f6', // Light gray background like WhatsApp/Telegram
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            height: containerHeight || '70vh', // Fallback height
        },
        systemMessageContainer: {
            display: 'flex',
            justifyContent: 'center',
            margin: '8px 0',
        },
        systemMessageBubble: {
            backgroundColor: '#e5e7eb',
            color: '#4b5563',
            fontSize: '0.75rem',
            padding: '4px 12px',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        },
        messageRow: (isSender) => ({
            display: 'flex',
            justifyContent: isSender ? 'flex-end' : 'flex-start',
            width: '100%',
        }),
        messageBubble: (isSender) => ({
            maxWidth: '75%',
            minWidth: '120px', // Ensure small messages aren't too thin
            padding: '10px 14px',
            borderRadius: '12px',
            borderTopRightRadius: isSender ? '0' : '12px',
            borderTopLeftRadius: isSender ? '12px' : '0',
            backgroundColor: isSender ? '#2563eb' : 'white', // Blue for sender, White for receiver
            color: isSender ? 'white' : '#1f2937',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            position: 'relative',
            wordWrap: 'break-word',
        }),
        senderName: {
            fontSize: '0.75rem',
            fontWeight: 'bold',
            color: '#d97706', // Amber-600 color for names in groups
            marginBottom: '4px',
            display: 'block',
        },
        sharedPostContainer: (isSender) => ({
            marginTop: '8px',
            marginBottom: '8px',
            padding: '10px',
            borderRadius: '8px',
            backgroundColor: isSender ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6', // Translucent on blue, gray on white
            cursor: 'pointer',
            border: isSender ? '1px solid rgba(255,255,255,0.3)' : '1px solid #e5e7eb',
        }),
        postImage: {
            width: '100%',
            height: '150px',
            objectFit: 'cover',
            borderRadius: '6px',
            marginBottom: '8px',
        },
        fileLink: (isSender, isHovered) => ({
            display: 'flex',
            alignItems: 'center',
            padding: '8px',
            borderRadius: '6px',
            textDecoration: 'none',
            backgroundColor: isSender 
                ? (isHovered ? '#1d4ed8' : '#1e40af') // Darker blue on hover
                : (isHovered ? '#e5e7eb' : '#f3f4f6'), // Gray on hover
            color: isSender ? 'white' : '#1f2937',
            border: isSender ? '1px solid rgba(255,255,255,0.2)' : '1px solid #e5e7eb',
            transition: 'background-color 0.2s',
            marginTop: '4px',
            marginBottom: '4px',
        }),
        messageText: (isSender) => ({
            fontSize: '0.95rem',
            lineHeight: '1.4',
            margin: 0,
            color: isSender ? 'white' : '#1f2937',
        }),
        timestamp: (isSender) => ({
            display: 'block',
            fontSize: '0.7rem',
            marginTop: '4px',
            textAlign: 'right',
            color: isSender ? 'rgba(255, 255, 255, 0.8)' : '#9ca3af',
        }),
        // Hover state management helper
        linkHover: {
            cursor: 'pointer'
        }
    };

    // Component for individual file link to handle hover state
    const FileLink = ({ msg, isSender }) => {
        const [isHovered, setIsHovered] = useState(false);
        return (
            <a
                href={msg.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.fileLink(isSender, isHovered)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <MdInsertDriveFile size={20} style={{ marginRight: '8px' }} />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.85rem', fontWeight: '500' }}>
                    {msg.fileName || 'Document'}
                </span>
            </a>
        );
    };


    return (
        <div style={styles.container}>
            {messages.map((msg) => {
                const isSender = msg.senderId === user.uid;
                
                // --- 1. System Messages (Center Aligned) ---
                if (msg.isSystemMessage || msg.senderId === 'system' || msg.senderId === 'SYSTEM') {
                    return (
                        <div key={msg.id} style={styles.systemMessageContainer}>
                            <div style={styles.systemMessageBubble}>
                                <MdInfoOutline size={14} style={{ marginRight: '4px' }} />
                                <span>{msg.content}</span>
                            </div>
                        </div>
                    );
                }

                // --- 2. Normal Messages (Left/Right Aligned) ---
                return (
                    <div key={msg.id} style={styles.messageRow(isSender)}>
                        <div style={styles.messageBubble(isSender)}>
                            
                            {/* Sender Name (Only for received messages in Group Chat) */}
                            {isGroup && !isSender && (
                                <span style={styles.senderName}>
                                    {getSenderName(msg.senderId)}
                                </span>
                            )}

                            {/* Shared Post Content */}
                            {msg.sharedPost && (
                                <div
                                    onClick={() => handleSharedPostClick(msg.sharedPost.id)}
                                    style={styles.sharedPostContainer(isSender)}
                                >
                                    <p style={{ fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '4px', color: isSender ? 'white' : '#4b5563' }}>
                                        Shared Post
                                    </p>
                                    {msg.sharedPost.imageUrl && (
                                        <img
                                            src={msg.sharedPost.imageUrl}
                                            alt="Post"
                                            style={styles.postImage}
                                        />
                                    )}
                                    <p style={{ fontSize: '0.85rem', color: isSender ? 'rgba(255,255,255,0.9)' : '#374151', margin: 0 }}>
                                        {renderPostContent(msg.sharedPost.content)}
                                    </p>
                                </div>
                            )}

                            {/* Main Message Content */}
                            {(msg.content || msg.fileUrl) && (
                                <>
                                    {msg.fileUrl && (
                                        <FileLink msg={msg} isSender={isSender} />
                                    )}
                                    
                                    {/* Text Content */}
                                    {(msg.content || (!msg.fileUrl && !msg.sharedPost)) && (
                                        <p style={styles.messageText(isSender)}>
                                            {msg.content}
                                        </p>
                                    )}
                                </>
                            )}

                            {/* Timestamp & Status */}
                            <span style={styles.timestamp(isSender)}>
                                {msg.isSending 
                                    ? 'Sending...' 
                                    : (msg.timestamp?.toDate ? msg.timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Just now')
                                }
                            </span>
                        </div>
                    </div>
                );
            })}
            <div ref={messagesEndRef} />
        </div>
    );
};

export default MessageDisplay;